import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, MessageCircle, Sun } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { IMAGES } from "@/data/content";

export default function Hero({ onSimulate, onWhatsapp }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} id="topo" className="relative min-h-[100svh] flex items-end overflow-hidden bg-secondary">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Painéis solares" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-secondary/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/20 to-transparent" />
      </motion.div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-4 py-1.5 mb-7"
        >
          <Sun size={15} className="text-accent" />
          <span className="overline text-white/90">Energia Solar • Bahia & Pernambuco</span>
        </motion.div>

        <h1 className="font-display font-extrabold text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tighter max-w-4xl">
          <MaskLine delay={0.25}>Sua conta de luz</MaskLine>
          <MaskLine delay={0.4} className="text-primary">quase zerada.</MaskLine>
          <MaskLine delay={0.55}>O sol trabalha</MaskLine>
          <MaskLine delay={0.7} className="italic font-light">por você.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-7 max-w-xl text-white/80 text-base sm:text-lg leading-relaxed"
        >
          Projetos solares sob medida para residências e empresas. Economize até 95% já no primeiro mês, com retorno garantido do investimento.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-9 flex flex-col sm:flex-row gap-3"
        >
          <button
            onClick={onSimulate}
            className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-accent text-accent-foreground font-bold hover:brightness-105 active:scale-[0.98] transition-all"
            data-testid="hero-simulate-btn"
          >
            Simular minha economia
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={onWhatsapp}
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-primary text-primary-foreground font-bold hover:brightness-105 active:scale-[0.98] transition-all"
            data-testid="hero-whatsapp-btn"
          >
            <MessageCircle size={18} /> Falar no WhatsApp
          </button>
        </motion.div>
      </div>
    </section>
  );
}
